import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  XMarkIcon,
  PlusIcon,
  TrashIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { alertApi, api } from '../../lib/api';

/**
 * Manage a team's bank-specific ALERT code map (Collateral / Type / Note Purpose / FFIEC
 * decodes). This is the durable, low-friction alternative to pre-appending decode columns to the
 * trial-balance file (Michael Wallace's roadmap, 2026-06-04): upload a raw file, see the unmapped
 * codes, then fill them in here or import a CSV. DISPLAY-ONLY — decodes never change analysis math.
 *
 * Optional `prefill` seeds blank rows for codes a file uses but the map doesn't decode yet, so the
 * "you have N unmapped codes" prompt lands the user straight into filling them.
 */

const CODE_TYPE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'COLLATERAL', label: 'Collateral' },
  { value: 'TYPE', label: 'Type' },
  { value: 'NOTE_PURPOSE', label: 'Note Purpose' },
  { value: 'FFIEC', label: 'FFIEC' },
];
const LABEL: Record<string, string> = Object.fromEntries(CODE_TYPE_OPTIONS.map((o) => [o.value, o.label]));

interface CodeMapRow {
  id: string;
  codeType: string;
  code: string;
  decode: string;
  source: string;
}
/** A row being edited locally. `id` is the server id for existing rows; new rows have a temp key. */
interface DraftRow {
  key: string;
  id?: string;
  codeType: string;
  code: string;
  decode: string;
  source?: string;
}

let tempCounter = 0;
const nextKey = () => `new-${tempCounter++}`;

export default function AlertCodeMapModal({
  isOpen,
  onClose,
  prefill,
}: {
  isOpen: boolean;
  onClose: () => void;
  /** Codes a file uses that aren't decoded yet — seeded as blank rows to fill. */
  prefill?: Array<{ codeType: string; code: string }>;
}) {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [rows, setRows] = useState<DraftRow[]>([]);

  const { data, isLoading } = useQuery({
    queryKey: ['alert-code-maps'],
    queryFn: async () => (await alertApi.getCodeMaps()).data as { entries: CodeMapRow[] },
    enabled: isOpen,
  });

  // Seed local editable rows from the server map (+ any prefill codes not already present).
  useEffect(() => {
    if (!isOpen || !data) return;
    const existing: DraftRow[] = (data.entries ?? []).map((e) => ({
      key: e.id,
      id: e.id,
      codeType: e.codeType,
      code: e.code,
      decode: e.decode,
      source: e.source,
    }));
    const have = new Set(existing.map((r) => `${r.codeType} ${r.code}`));
    const seeds: DraftRow[] = (prefill ?? [])
      .filter((p) => !have.has(`${p.codeType} ${p.code}`))
      .map((p) => ({ key: nextKey(), codeType: p.codeType, code: p.code, decode: '' }));
    setRows([...seeds, ...existing]);
  }, [isOpen, data, prefill]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const entries = rows
        .map((r) => ({ codeType: r.codeType, code: r.code.trim(), decode: r.decode.trim() }))
        .filter((r) => r.code && r.decode);
      if (entries.length === 0) throw new Error('Nothing to save — add a code and a description.');
      return (await alertApi.putCodeMaps(entries)).data as { upserted: number; skipped: number };
    },
    onSuccess: (res) => {
      toast.success(`Saved ${res.upserted} code${res.upserted === 1 ? '' : 's'}.`);
      queryClient.invalidateQueries({ queryKey: ['alert-code-maps'] });
      queryClient.invalidateQueries({ queryKey: ['alert-analysis'] });
      queryClient.invalidateQueries({ queryKey: ['alert-unmapped'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.error || e?.message || 'Save failed.'),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => alertApi.deleteCodeMap(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alert-code-maps'] });
      queryClient.invalidateQueries({ queryKey: ['alert-analysis'] });
      queryClient.invalidateQueries({ queryKey: ['alert-unmapped'] });
    },
    onError: () => toast.error('Could not delete that entry.'),
  });

  const importMutation = useMutation({
    mutationFn: async (file: File) =>
      (await alertApi.importCodeMapCsv(file)).data as { upserted: number; skipped: number; rowErrors: string[] },
    onSuccess: (res) => {
      const errs = res.rowErrors?.length ? ` (${res.rowErrors.length} row${res.rowErrors.length === 1 ? '' : 's'} skipped)` : '';
      toast.success(`Imported ${res.upserted} code${res.upserted === 1 ? '' : 's'}${errs}.`);
      queryClient.invalidateQueries({ queryKey: ['alert-code-maps'] });
      queryClient.invalidateQueries({ queryKey: ['alert-analysis'] });
      queryClient.invalidateQueries({ queryKey: ['alert-unmapped'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.error || 'Import failed — check the CSV columns.'),
  });

  const downloadTemplate = async () => {
    try {
      const res = await api.get(alertApi.codeMapTemplateUrl(), { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'clarid_alert_code_map_template.csv';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Could not download the template.');
    }
  };

  const setRow = (key: string, patch: Partial<DraftRow>) =>
    setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  const addRow = () =>
    setRows((rs) => [{ key: nextKey(), codeType: 'COLLATERAL', code: '', decode: '' }, ...rs]);

  const removeRow = (row: DraftRow) => {
    setRows((rs) => rs.filter((r) => r.key !== row.key));
    if (row.id) deleteMutation.mutate(row.id);
  };

  const onPickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) importMutation.mutate(f);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const unfilledPrefill = useMemo(
    () => rows.filter((r) => !r.id && !r.decode.trim() && r.code.trim()).length,
    [rows],
  );

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100"
          leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100"
              leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-3xl bg-white rounded-xl shadow-xl p-6">
                <div className="flex items-center justify-between mb-1">
                  <Dialog.Title className="text-lg font-semibold text-gray-900">
                    Bank Code Map
                  </Dialog.Title>
                  <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
                <p className="text-sm text-gray-500 mb-4">
                  Turn your trial-balance codes (Collateral, Type, Note Purpose) into readable labels.
                  This map is saved for your institution and reused on every file — no need to append
                  decode columns. Labels are for display only and never change the analysis numbers.
                </p>

                {/* Toolbar */}
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <button
                    onClick={addRow}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    <PlusIcon className="h-4 w-4" /> Add code
                  </button>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={importMutation.isPending}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    <ArrowUpTrayIcon className="h-4 w-4" /> {importMutation.isPending ? 'Importing…' : 'Import CSV'}
                  </button>
                  <button
                    onClick={downloadTemplate}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    <ArrowDownTrayIcon className="h-4 w-4" /> Template
                  </button>
                  <input ref={fileInputRef} type="file" accept=".csv,text/csv" className="hidden" onChange={onPickFile} />
                </div>

                {/* Table */}
                <div className="max-h-[50vh] overflow-y-auto rounded-lg border border-gray-100">
                  {isLoading ? (
                    <div className="p-6 text-sm text-gray-400">Loading…</div>
                  ) : rows.length === 0 ? (
                    <div className="p-6 text-sm text-gray-400">
                      No codes yet. Add one, or import a CSV.
                    </div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                        <tr>
                          <th className="px-3 py-2 text-left font-medium">Type</th>
                          <th className="px-3 py-2 text-left font-medium">Code</th>
                          <th className="px-3 py-2 text-left font-medium">Description</th>
                          <th className="px-3 py-2 w-10" />
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {rows.map((r) => (
                          <tr key={r.key} className={!r.id && !r.decode.trim() && r.code.trim() ? 'bg-amber-50/60' : ''}>
                            <td className="px-3 py-1.5">
                              <select
                                value={r.codeType}
                                onChange={(e) => setRow(r.key, { codeType: e.target.value })}
                                className="rounded border border-gray-200 px-2 py-1 text-sm bg-white"
                              >
                                {CODE_TYPE_OPTIONS.map((o) => (
                                  <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                              </select>
                            </td>
                            <td className="px-3 py-1.5">
                              <input
                                value={r.code}
                                onChange={(e) => setRow(r.key, { code: e.target.value })}
                                placeholder="e.g. 21"
                                className="w-24 rounded border border-gray-200 px-2 py-1 text-sm font-mono"
                              />
                            </td>
                            <td className="px-3 py-1.5">
                              <input
                                value={r.decode}
                                onChange={(e) => setRow(r.key, { decode: e.target.value })}
                                placeholder={`e.g. ${LABEL[r.codeType] === 'Type' ? 'Consumer Non-RE Term' : 'Unsecured'}`}
                                className="w-full rounded border border-gray-200 px-2 py-1 text-sm"
                              />
                            </td>
                            <td className="px-3 py-1.5 text-right">
                              <button
                                onClick={() => removeRow(r)}
                                className="text-gray-300 hover:text-red-500"
                                title="Remove"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                <div className="mt-5 flex items-center justify-between">
                  <p className="text-xs text-gray-400">
                    {unfilledPrefill > 0
                      ? `${unfilledPrefill} unmapped code${unfilledPrefill === 1 ? '' : 's'} still need a description.`
                      : ''}
                  </p>
                  <div className="flex items-center gap-2">
                    <button onClick={onClose} className="rounded-lg px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50">
                      Close
                    </button>
                    <button
                      onClick={() => saveMutation.mutate()}
                      disabled={saveMutation.isPending}
                      className="rounded-lg bg-ocean-600 px-4 py-2 text-sm font-semibold text-white hover:bg-ocean-700 disabled:opacity-50"
                    >
                      {saveMutation.isPending ? 'Saving…' : 'Save changes'}
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
